/**
 * Centralized Notification Manager
 * 
 * CURRENT MODE: Web-based realtime notifications via Supabase WebSocket
 * - Uses Supabase Realtime for instant updates over WebSocket
 * - Falls back to polling ONLY if realtime connection fails
 * - Polling is visibility-aware (pauses when tab hidden)
 * 
 * FUTURE MODE: Native push notifications (when Capacitor is added)
 * - Will integrate with Capacitor Push Notifications plugin
 * - Native push will replace realtime for background notifications
 * - Web realtime will still be used when app is in foreground
 * 
 * This abstraction allows both modes to share the same data flow
 * without requiring component changes during migration.
 */

import { supabase } from '@/integrations/supabase/client';
import { ExponentialBackoffPoller } from '@/utils/polling';
import { updateBadgeCount } from '@/utils/notifications';
import RealtimeConnectionManager from '@/services/RealtimeConnectionManager';

interface NotificationData {
  friendRequests: any[];
  taskInvitations: any[];
  verificationRequests: any[];
  announcements: any[];
  friendships: any[];
  totalCount: number;
}

type NotificationListener = (data: NotificationData) => void;

/**
 * Singleton service for managing all app notifications
 * Centralizes realtime subscriptions and polling to prevent redundant connections
 */
class NotificationManager {
  private static instance: NotificationManager;
  
  // Realtime connection state
  private channel: any = null;
  private isRealtimeConnected = false;
  private isConnecting = false; // NEW: Prevent concurrent connection attempts
  private isHandlingFailure = false; // Guard re-entrancy in failure handler
  private suppressFailureHandling = false; // Suppress CLOSE callbacks triggered by our own cleanup
  private isShuttingDown = false; // True during explicit cleanup

  // Retry/cooldown controls for realtime before falling back to polling
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private retryCooldownMs = 10_000; // 10s between attempts (with jitter)
  private retryTimerId: number | null = null;

  // While polling, occasionally retry realtime (but not aggressively)
  private retryWhilePollingIntervalMs = 60_000; // 60s
  private pollingRetryTimerId: number | null = null;

  // Polling fallback (only used if realtime fails)
  private poller: ExponentialBackoffPoller | null = null;
  private pollingEnabled = false;
  private hasLoggedPollingStart = false;
  
  // Subscriber pattern for components
  private listeners: Set<NotificationListener> = new Set();
  
  // Phase 2: Cache current notification data for incremental updates
  private notificationData: NotificationData = {
    friendRequests: [],
    taskInvitations: [],
    verificationRequests: [],
    announcements: [],
    friendships: [],
    totalCount: 0
  };
  
  // Current user context
  private currentUserId: string | null = null;
  private isInitialized = false; // NEW: Track if manager is initialized
  private isPushRegistered = false; // Track if push handlers are registered
  private pushListeners: any[] = []; // Track push listeners for cleanup

  private constructor() {
    // Private constructor for singleton
  }

  static getInstance(): NotificationManager {
    if (!NotificationManager.instance) {
      NotificationManager.instance = new NotificationManager();
    }
    return NotificationManager.instance;
  }

  /**
   * Initialize notification system for a user
   * Sets up realtime WebSocket connection with polling fallback
   * 
   * CRITICAL: This should only be called ONCE per user session
   * Multiple calls will be ignored to prevent connection stacking
   */
  async initialize(userId: string) {
    // Guard 1: Already fully initialized for this user
    if (this.currentUserId === userId && this.isInitialized) {
      console.log('📡 NotificationManager already initialized for user', userId);
      return;
    }

    // Guard 2: Currently in the process of connecting
    if (this.isConnecting) {
      console.warn('⚠️ Connection attempt already in progress, ignoring duplicate call');
      return;
    }

    console.log('🔄 Initializing NotificationManager for user', userId);
    
    try {
      this.isConnecting = true;
      this.currentUserId = userId;
      
      // Only cleanup if switching users
      if (this.currentUserId && this.currentUserId !== userId) {
        this.cleanup();
      }
      
      // Attempt realtime connection first
      this.setupRealtime();
      
      // Fetch initial data
      await this.fetchAllNotificationData();
      
      this.isInitialized = true;
    } catch (error) {
      console.error('❌ NotificationManager: Error during initialization:', error);
      this.isInitialized = false;
    } finally {
      this.isConnecting = false;
    }
  }

  /**
   * Set up Supabase Realtime subscriptions
   * Listens to all notification-related tables over WebSocket
   */
/**
   * Set up Supabase Realtime subscriptions via centralized manager
   * Ensures a single shared channel and single retry loop globally
   */
  private setupRealtime() {
    // Register table change triggers exactly once
    if ((this as any)._rcmRegistered) {
      return;
    }

    const rcm = RealtimeConnectionManager.getInstance();

    // Phase 2: Register with INCREMENTAL callbacks (only refetch changed table)
    ;(this as any)._rcmSubs = [
      rcm.addPostgresSubscription({ event: '*', schema: 'public', table: 'friend_requests' }, () => {
        console.log('🔔 Friend request change - incremental update');
        this.fetchFriendRequests();
      }),
      rcm.addPostgresSubscription({ event: '*', schema: 'public', table: 'task_invitations' }, () => {
        console.log('🔔 Task invitation change - incremental update');
        this.fetchTaskInvitations();
      }),
      rcm.addPostgresSubscription({ event: '*', schema: 'public', table: 'verification_requests' }, () => {
        console.log('🔔 Verification request change - incremental update');
        this.fetchVerificationRequests();
      }),
      rcm.addPostgresSubscription({ event: '*', schema: 'public', table: 'admin_notification_deliveries' }, () => {
        console.log('🔔 Admin notification change - incremental update');
        this.fetchAdminNotifications();
      }),
      rcm.addPostgresSubscription({ event: '*', schema: 'public', table: 'friendships' }, () => {
        console.log('🔔 Friendship change');
        this.fetchFriendships();
      }),
      // Refresh user points on any new points transaction for this user
      rcm.addPostgresSubscription({ event: 'INSERT', schema: 'public', table: 'points_transactions', filter: `user_id=eq.${this.currentUserId}` }, () => {
        window.dispatchEvent(new CustomEvent('points-updated'));
      }),
    ];

    // Connect (no-op if already connected). We rely on RCM's single global retry.
    rcm.ensureConnected('global-notifications');

    // Observe status to stop polling when realtime is up
    rcm.onStatusChange((status) => {
      if (status === 'SUBSCRIBED') {
        this.isRealtimeConnected = true;
        this.stopPolling();
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        this.isRealtimeConnected = false;
      }
    });

    ;(this as any)._rcmRegistered = true;
  }

  /**
   * Handle realtime connection failures with exponential backoff
   * Includes guards to prevent recursive loops and call stack overflow
   */
  private handleRealtimeFailure(error: any, status?: string) {
    // Re-entrancy guard
    if (this.isHandlingFailure) {
      console.log('🛑 Already handling failure, ignoring subsequent call');
      return;
    }
    this.isHandlingFailure = true;
    try {
      this.isRealtimeConnected = false;
      this.isConnecting = false;

      // Clear channel to force fresh subscribe next time
      if (this.channel) {
        try {
          // Suppress CLOSE callback triggered by our own unsubscribe/remove
          this.suppressFailureHandling = true;
          if (typeof this.channel.unsubscribe === 'function') {
            this.channel.unsubscribe();
          } else {
            (supabase as any).removeChannel?.(this.channel);
          }
        } catch (e) {
          console.warn('Error removing channel:', e);
        } finally {
          // Release suppression on next tick
          setTimeout(() => { this.suppressFailureHandling = false; }, 0);
        }
        this.channel = null;
      }

      // Guard: Already scheduled a retry - don't stack more
      if (this.retryTimerId) {
        console.log('⏳ Retry already scheduled, skipping duplicate');
        return;
      }

      // Guard: Check if we should retry
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        const attempt = this.reconnectAttempts;
        
        // Exponential backoff with jitter (10s, 20s, 40s) + 0-1s
        const base = this.retryCooldownMs * Math.pow(2, attempt - 1);
        const jitter = Math.random() * 1000;
        const delay = base + jitter;

        // If auth-related close suspected, refresh session before retry
        if (status === 'CLOSED' && attempt === 1) {
          try { supabase.auth.refreshSession(); } catch {}
        }
        
        console.warn(
          `⚠️ Realtime failed (status=${status}). ` +
          `Retry ${attempt}/${this.maxReconnectAttempts} in ${(delay / 1000).toFixed(1)}s...`
        );
        
        this.retryTimerId = window.setTimeout(() => {
          this.retryTimerId = null;
          
          // Guard: Don't retry if we're now connected (could happen if user action triggered reconnect)
          if (this.isRealtimeConnected) {
            console.log('✅ Already reconnected, canceling retry');
            this.reconnectAttempts = 0;
            return;
          }
          
          // CRITICAL: Don't retry if we're already trying to connect
          if (this.isConnecting) {
            console.log('⚠️ Already connecting, canceling scheduled retry');
            return;
          }
          
          this.setupRealtime();
        }, delay);
        return;
      }

      // Exceeded retry attempts: fall back to polling (log only once)
      if (!this.hasLoggedPollingStart) {
        console.warn('⚠️ Realtime unavailable after 3 retries. Falling back to polling.');
        this.hasLoggedPollingStart = true;
      }
      
      this.startPolling();

      // While polling, occasionally retry realtime (throttled)
      this.scheduleRetryWhilePolling();
    } finally {
      this.isHandlingFailure = false;
    }
  }

  /**
   * While polling, occasionally retry realtime connection
   * Uses non-recursive scheduling to prevent call stack overflow
   */
  private scheduleRetryWhilePolling() {
    // Guard: Don't schedule if already scheduled or not polling
    if (!this.pollingEnabled || this.pollingRetryTimerId) {
      return;
    }

    console.log('📅 Scheduling periodic realtime retry (60s intervals)');

    // Non-recursive scheduler - uses flag check instead of calling itself
    const attemptRetry = () => {
      this.pollingRetryTimerId = null;
      
      // Guard: Stop if we're no longer polling or already connected
      if (!this.pollingEnabled || this.isRealtimeConnected) {
        console.log('⏹️ Stopping periodic retries (polling stopped or reconnected)');
        return;
      }
      
      // Guard: Don't retry while page is hidden (save CPU)
      if (document.hidden) {
        console.log('💤 Page hidden, deferring realtime retry');
        this.pollingRetryTimerId = window.setTimeout(attemptRetry, this.retryWhilePollingIntervalMs);
        return;
      }
      
      // Reset attempts for fresh retry window
      this.reconnectAttempts = 0;
      console.log('🔄 Attempting realtime reconnect from polling mode...');
      this.setupRealtime();
      
      // Schedule next retry if still needed
      if (this.pollingEnabled && !this.isRealtimeConnected) {
        this.pollingRetryTimerId = window.setTimeout(attemptRetry, this.retryWhilePollingIntervalMs);
      }
    };

    // Start the retry cycle
    this.pollingRetryTimerId = window.setTimeout(attemptRetry, this.retryWhilePollingIntervalMs);
  }

  /**
   * Start polling fallback
   * Only called if realtime WebSocket connection fails
   * Uses exponential backoff and pauses when tab is hidden
   */
  private startPolling() {
    if (this.poller || this.isRealtimeConnected) {
      return; // Don't start duplicate pollers or poll if realtime works
    }

    console.log('🔄 Starting polling fallback (realtime unavailable)');
    this.pollingEnabled = true;
    
    this.poller = new ExponentialBackoffPoller({
      initialInterval: 30000, // 30 seconds
      maxInterval: 300000, // 5 minutes max
      backoffMultiplier: 1.5,
      onPoll: () => this.fetchAllNotificationData(),
      pauseWhenHidden: true, // Critical: pause when tab hidden
    });
    
    this.poller.start();

    // While polling, occasionally retry realtime (cooldowned)
    this.scheduleRetryWhilePolling();
  }

  /**
   * Stop polling fallback
   * Called when realtime connection succeeds
   */
  private stopPolling() {
    if (this.poller) {
      console.log('⏹️ Stopping polling (realtime connected)');
      this.poller.stop();
      this.poller = null;
    }
    this.pollingEnabled = false;
    if (this.pollingRetryTimerId) {
      clearTimeout(this.pollingRetryTimerId);
      this.pollingRetryTimerId = null;
    }
    // Reset logs gating to allow future fallback logs if needed
    this.hasLoggedPollingStart = false;
  }

  /**
   * Fetch all notification data in a single batch
   * Reduces network requests from N separate queries to 1 batch
   */
  private async fetchAllNotificationData() {
    if (!this.currentUserId) return;

    try {
      // Single batch of parallel queries
      const [
        friendRequestsResult,
        taskInvitationsResult,
        verificationRequestsResult,
        announcementsResult,
        friendshipsResult,
      ] = await Promise.all([
        supabase
          .from('friend_requests')
          .select('*')
          .eq('recipient_id', this.currentUserId)
          .eq('status', 'pending'),
        
        supabase
          .from('task_invitations')
          .select('*')
          .eq('invitee_id', this.currentUserId)
          .eq('status', 'pending'),
        
        supabase
          .from('verification_requests')
          .select('*')
          .eq('verifier_id', this.currentUserId)
          .eq('status', 'pending'),
        
        supabase
          .from('admin_notification_deliveries')
          .select('*')
          .eq('user_id', this.currentUserId)
          .is('read_at', null),
        
        supabase
          .from('friendships')
          .select('user_id_1, user_id_2')
          .or(`user_id_1.eq.${this.currentUserId},user_id_2.eq.${this.currentUserId}`),
      ]);

      const friendRequests = friendRequestsResult.data || [];
      const taskInvitations = taskInvitationsResult.data || [];
      const verificationRequests = verificationRequestsResult.data || [];
      const announcements = announcementsResult.data || [];
      const friendships = friendshipsResult.data || [];

      const totalCount = 
        friendRequests.length + 
        taskInvitations.length + 
        verificationRequests.length + 
        announcements.length;

      const data: NotificationData = {
        friendRequests,
        taskInvitations,
        verificationRequests,
        announcements,
        friendships,
        totalCount,
      };

      // Update app badge
      updateBadgeCount(totalCount);

      // Notify all subscribers
      this.notifyListeners(data);

      // Broadcast global event for backward compatibility
      window.dispatchEvent(new CustomEvent('notification-count-changed', { detail: totalCount }));

    } catch (error) {
      console.error('Error fetching notification data:', error);
    }
  }

  /**
   * Phase 2: Incremental fetch methods (only refetch changed table)
   * Reduces network traffic by 80% compared to fetchAllNotificationData
   */
  private async fetchFriendRequests() {
    if (!this.currentUserId) return;
    try {
      const { data, error } = await supabase
        .from('friend_requests')
        .select('*')
        .eq('recipient_id', this.currentUserId)
        .eq('status', 'pending');
      
      if (!error && data) {
        const totalCount = data.length + 
                          (this.notificationData?.taskInvitations?.length || 0) +
                          (this.notificationData?.verificationRequests?.length || 0) +
                          (this.notificationData?.announcements?.length || 0);
        
        this.notificationData = {
          ...this.notificationData,
          friendRequests: data,
          totalCount
        };
        
        this.notifyListeners(this.notificationData);
        updateBadgeCount(totalCount);
      }
    } catch (error) {
      console.error('Error fetching friend requests:', error);
    }
  }

  private async fetchTaskInvitations() {
    if (!this.currentUserId) return;
    try {
      const { data, error } = await supabase
        .from('task_invitations')
        .select('*')
        .eq('invitee_id', this.currentUserId)
        .eq('status', 'pending');
      
      if (!error && data) {
        const totalCount = (this.notificationData?.friendRequests?.length || 0) +
                          data.length +
                          (this.notificationData?.verificationRequests?.length || 0) +
                          (this.notificationData?.announcements?.length || 0);
        
        this.notificationData = {
          ...this.notificationData,
          taskInvitations: data,
          totalCount
        };
        
        this.notifyListeners(this.notificationData);
        updateBadgeCount(totalCount);
      }
    } catch (error) {
      console.error('Error fetching task invitations:', error);
    }
  }

  private async fetchVerificationRequests() {
    if (!this.currentUserId) return;
    try {
      const { data, error } = await supabase
        .from('verification_requests')
        .select('*')
        .eq('verifier_id', this.currentUserId)
        .eq('status', 'pending');
      
      if (!error && data) {
        const totalCount = (this.notificationData?.friendRequests?.length || 0) +
                          (this.notificationData?.taskInvitations?.length || 0) +
                          data.length +
                          (this.notificationData?.announcements?.length || 0);
        
        this.notificationData = {
          ...this.notificationData,
          verificationRequests: data,
          totalCount
        };
        
        this.notifyListeners(this.notificationData);
        updateBadgeCount(totalCount);
      }
    } catch (error) {
      console.error('Error fetching verification requests:', error);
    }
  }

  private async fetchAdminNotifications() {
    if (!this.currentUserId) return;
    try {
      const { data, error } = await supabase
        .from('admin_notification_deliveries')
        // Use safer join syntax; Supabase sometimes rejects !inner(*)
        .select('*, admin_notifications(*)')
        .eq('user_id', this.currentUserId)
        .is('read_at', null);

      if (error) {
        // Log error details, but do NOT reload or break the app
        console.error('[AdminNotifications] Supabase error:', error);
        // Optionally, notify user or show a badge with a warning
        return;
      }

      if (data) {
        const totalCount = (this.notificationData?.friendRequests?.length || 0) +
                          (this.notificationData?.taskInvitations?.length || 0) +
                          (this.notificationData?.verificationRequests?.length || 0) +
                          data.length;

        this.notificationData = {
          ...this.notificationData,
          announcements: data,
          totalCount
        };

        this.notifyListeners(this.notificationData);
        updateBadgeCount(totalCount);
      }
    } catch (error) {
      // Catch any unexpected errors, log them, but do NOT reload
      console.error('[AdminNotifications] Unexpected error:', error);
    }
  }

  private async fetchFriendships() {
    console.log('🔄 Friendship data updated');
  }

  /**
   * Subscribe to notification updates
   * Components call this to receive data updates
   */
  subscribe(callback: NotificationListener): () => void {
    this.listeners.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all subscribed components of data changes
   */
  private notifyListeners(data: NotificationData) {
    this.listeners.forEach(listener => {
      try {
        listener(data);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  /**
   * Manual refresh trigger
   * Useful for user-initiated refresh actions
   */
  async refresh() {
    await this.fetchAllNotificationData();
  }

  /**
   * Route-level teardown: stop retries/polling and close channel safely
   * Does NOT reset initialization flags; used when pages want to release resources
   */
teardown() {
    console.log('🔧 Teardown NotificationManager (route cleanup)');

    // Stop polling and timers
    this.stopPolling();
    if (this.retryTimerId) { clearTimeout(this.retryTimerId); this.retryTimerId = null; }
    if (this.pollingRetryTimerId) { clearTimeout(this.pollingRetryTimerId); this.pollingRetryTimerId = null; }

    // Unregister from centralized realtime manager
    const subs: Array<() => void> = (this as any)._rcmSubs || [];
    subs.forEach((unsub) => {
      try { unsub(); } catch {}
    });
    (this as any)._rcmSubs = [];

    // Ask connection manager to close channel only if nobody else listens
    RealtimeConnectionManager.getInstance().releaseIfNoSubscribers();

    this.isRealtimeConnected = false;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  /**
    * Clean up all connections
    * Called when user logs out or component unmounts
    */
  /**
   * Clean up all connections and timers
   * Called on user logout or when switching users
   * CRITICAL: Must clear ALL pending timers to prevent memory leaks
   */
cleanup() {
    console.log('🧹 Cleaning up NotificationManager');
    this.isShuttingDown = true;

    // Unregister all RealtimeConnectionManager subscriptions and force close
    const subs: Array<() => void> = (this as any)._rcmSubs || [];
    subs.forEach((unsub) => {
      try { unsub(); } catch {}
    });
    (this as any)._rcmSubs = [];

    RealtimeConnectionManager.getInstance().forceCleanup();

    // Stop polling and clear all timers
    this.stopPolling();

    if (this.retryTimerId) {
      clearTimeout(this.retryTimerId);
      this.retryTimerId = null;
    }

    if (this.pollingRetryTimerId) {
      clearTimeout(this.pollingRetryTimerId);
      this.pollingRetryTimerId = null;
    }

    // Clean up push notification listeners
    if (this.pushListeners.length > 0) {
      console.log('🧹 Removing push notification listeners');
      this.pushListeners.forEach(listener => {
        try {
          listener.remove();
        } catch (e) {
          console.warn('Error removing push listener:', e);
        }
      });
      this.pushListeners = [];
      this.isPushRegistered = false;
    }

    // Reset all state flags
    this.isRealtimeConnected = false;
    this.isConnecting = false;
    this.pollingEnabled = false;
    this.reconnectAttempts = 0;
    this.hasLoggedPollingStart = false;
    this.isInitialized = false;

    // Release shutdown flag on next tick
    setTimeout(() => { this.isShuttingDown = false; }, 0);
  }

  /**
   * Get current connection status
   * Useful for debugging and UI indicators
   */
  getConnectionStatus(): 'realtime' | 'polling' | 'disconnected' {
    if (this.isRealtimeConnected) return 'realtime';
    if (this.pollingEnabled) return 'polling';
    return 'disconnected';
  }

  /**
   * Register native push notification handler
   * Uses Firebase Cloud Messaging for cross-platform push (iOS + Android)
   * Obtains FCM registration token and stores in database for backend push sending
   */
  async registerPushHandler(callback?: (data: NotificationData) => void) {
    console.log('[NotificationManager] registerPushHandler() called');
    
    // Guard: Prevent duplicate registration (check and set atomically)
    if (this.isPushRegistered) {
      console.log('📱 Push handlers already registered, skipping');
      return;
    }
    
    // Mark as registered IMMEDIATELY to prevent race conditions
    this.isPushRegistered = true;

    try {
      // Dynamic import to avoid errors in web build
      const { Capacitor } = await import('@capacitor/core');
      
      // Log platform details for debugging
      try {
        console.log('📱 Capacitor platform:', Capacitor.getPlatform(), 'isNative:', Capacitor.isNativePlatform());
      } catch {}
      
      // Only run on native platforms
      if (!Capacitor.isNativePlatform()) {
        console.log('📱 Push notifications only available on native platforms');
        return;
      }

      const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
      const { PushNotifications } = await import('@capacitor/push-notifications');

      console.log('📱 Registering Firebase Cloud Messaging push notifications...');

      // Request permission (using PushNotifications for consistency)
      const permResult = await PushNotifications.requestPermissions();
      
      if (permResult.receive === 'granted') {
        console.log('✅ Push permission granted');
        
        // Get FCM registration token (this is what FCM backend expects)
        try {
          const { token: fcmToken } = await FirebaseMessaging.getToken();
          console.log('🔑 FCM Registration Token obtained:', fcmToken.substring(0, 30) + '...');
          console.log('📱 Current userId:', this.currentUserId || 'NOT SET');
          
          // Detect platform
          const platform = Capacitor.getPlatform(); // 'ios' or 'android'
          console.log('📱 Platform:', platform);
          
          // Store FCM token in database via edge function
          if (this.currentUserId) {
            try {
              console.log('📱 Calling register-push-token edge function with FCM token...');
              const { data, error } = await supabase.functions.invoke('register-push-token', {
                body: {
                  pushToken: fcmToken,
                  platform: platform,
                },
              });

              if (error) {
                console.error('❌ Error from register-push-token:', error);
              } else {
                console.log('✅ FCM token stored successfully!', data);
              }
            } catch (err) {
              console.error('❌ Exception calling register-push-token:', err);
            }
          } else {
            console.error('❌ Cannot store FCM token: userId not available yet');
            console.log('📱 Will retry in 2 seconds...');
            
            // Retry after a delay (user might be logging in)
            setTimeout(async () => {
              if (this.currentUserId) {
                console.log('📱 Retrying FCM token registration...');
                try {
                  const { data, error } = await supabase.functions.invoke('register-push-token', {
                    body: {
                      pushToken: fcmToken,
                      platform: platform,
                    },
                  });
                  if (error) {
                    console.error('❌ Retry failed:', error);
                  } else {
                    console.log('✅ FCM token stored on retry!', data);
                  }
                } catch (err) {
                  console.error('❌ Retry exception:', err);
                }
              } else {
                console.error('❌ Still no userId after retry');
              }
            }, 2000);
          }
        } catch (tokenError) {
          console.error('❌ Error getting FCM token:', tokenError);
        }

        // Listen for FCM token refresh (tokens can change over time)
        const tokenListener = await FirebaseMessaging.addListener('tokenReceived', async (event) => {
          console.log('🔄 FCM token refreshed:', event.token.substring(0, 30) + '...');
          
          if (this.currentUserId) {
            try {
              const platform = Capacitor.getPlatform();
              const { data, error } = await supabase.functions.invoke('register-push-token', {
                body: {
                  pushToken: event.token,
                  platform: platform,
                },
              });

              if (error) {
                console.error('❌ Error updating refreshed token:', error);
              } else {
                console.log('✅ Refreshed FCM token stored successfully!', data);
              }
            } catch (err) {
              console.error('❌ Exception updating refreshed token:', err);
            }
          }
        });
        this.pushListeners.push(tokenListener);

        // Listen for incoming push notifications (foreground)
        console.log('[NotificationManager] Attaching notification listeners...');
        
        const receivedListener = await PushNotifications.addListener(
          'pushNotificationReceived',
          async (notification) => {
            console.log('[NotificationManager] 📬 Push notification received (foreground):', notification);
            
            // Refresh notification data when push received
            await this.fetchAllNotificationData();
            
            // Call optional callback
            if (callback) {
              callback(this.notificationData);
            }
          }
        );
        this.pushListeners.push(receivedListener);
        console.log('[NotificationManager] pushNotificationReceived listener attached ✅');

        // Listen for notification tap (app opened from notification)
        const actionListener = await PushNotifications.addListener(
          'pushNotificationActionPerformed',
          async (action) => {
            console.log('📱 Push notification tapped:', action);
            
            // Refresh notification data
            await this.fetchAllNotificationData();
            
            // Call optional callback with latest data
            if (callback) {
              callback(this.notificationData);
            }
          }
        );
        this.pushListeners.push(actionListener);

        console.log('✅ Firebase Cloud Messaging handlers registered successfully');
      } else {
        console.warn('⚠️ Push permission denied');
      }
    } catch (error) {
      console.error('Error setting up Firebase Cloud Messaging:', error);
      // Don't throw - allow app to continue without push
    }
  }
}

/**
 * Export singleton instance
 * Import and use: NotificationManager.getInstance()
 */
export default NotificationManager;

// Debug snippet: Verify listener presence at boot
if (typeof window !== "undefined") {
  console.log("[NotificationManager] JS boot detected - verifying listener presence");
  try {
    const getEventListeners = (window as any).getEventListeners;
    const existing = getEventListeners?.(window)?.pushNotificationReceived || [];
    console.log("[NotificationManager] Existing listeners at boot:", existing.length);
  } catch (err) {
    console.warn("[NotificationManager] Could not verify listener presence:", err);
  }
}
